import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { PrintLayout, PrinterProtocolType, PrinterState } from "@/data/model/Enums";
import type { IProductionSite } from "@/data/model/ProductionSite";
import { DateTime } from "@/data/util/DateTime";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";

export class ProductionSiteTableController {
    private $element: JQuery;
    private dao: CatalogDao;
    private dataTable: DataTable<IProductionSite>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);

        const tableConfig: IDataTableConfig<IProductionSite> = {
            columnMapping: [
                {
                    name: i18n("general.fields.designation"),
                    property: "name",
                },
                {
                    name: i18n("productionSite.fields.layout"),
                    property: "printLayout",
                    styles: { width: "19%" },
                    formatter: (m, p) => i18n("enums.PrintLayout." + PrintLayout[p]),
                },
                {
                    name: i18n("productionSite.fields.protocol"),
                    property: "printerProtocolType",
                    styles: { width: "9%" },
                    formatter: (m, p) => i18n("enums.PrinterProtocolType." + PrinterProtocolType[p]),
                },
                {
                    name: i18n("productionSite.fields.isActive"),
                    property: "isActive",
                    styles: { width: "8%" },
                    formatter: (m, p) => {
                        return (
                            '<div class="kt-badge ' +
                            (p ? "kt-badge--success" : "kt-badge--danger") +
                            ' kt-badge--inline">' +
                            (p ? i18n("general.yes") : i18n("general.no")) +
                            "</div>"
                        );
                    },
                },
                {
                    name: i18n("productionSite.fields.backupSite"),
                    property: "backupProductionSiteId",
                    styles: { width: "12%" },
                    formatter: (m, p) => {
                        if (p === undefined) return i18n("general.no");

                        return i18n("general.yes");
                    },
                },
                {
                    name: i18n("general.fields.state"),
                    property: "state",
                    styles: { width: "13%" },
                    formatter: (m, p) => {
                        let colorClass = "kt-badge--success";
                        switch (p) {
                            case PrinterState.OutOfPaper:
                                colorClass = "kt-badge--warning";
                                break;
                            case PrinterState.OpenTrail:
                                colorClass = "kt-badge--warning";
                                break;
                            case PrinterState.ErrorState:
                                colorClass = "kt-badge--danger";
                                break;
                            case PrinterState.Offline:
                                colorClass = "kt-badge--danger";
                                break;
                        }

                        return (
                            '<div class="kt-badge ' +
                            colorClass +
                            ' kt-badge--inline">' +
                            i18n("enums.PrinterState." + PrinterState[p]) +
                            "</div>"
                        );
                    },
                },
                {
                    name: i18n("productionSite.fields.lastCommunication"),
                    property: "lastCommunication",
                    styles: { width: "15%" },
                    formatter: (m, p) => {
                        return p === undefined ? "nie" : DateTime.toPreciseDateTimeString(p);
                    },
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: true,
            locationDependend: true,
            defaultSortProperty: "name",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.dataTable = new DataTable<IProductionSite>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.productionSiteDataSource,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/productionsite/" + m.id;
            },
            (m) => {
                window.open("/productionsite/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(() => {
            this.dataTable.loadData(true);
        }, this);
    }
}
